<template>
    <div class="home-leaderboard carto-card carto-card--white">
        <div class="home-leaderboard__header">
            <div>
                <h3 class="home-leaderboard__title">Palmarès</h3>
            </div>
            <v-select
                class="home-leaderboard__select"
                dense
                outlined
                hide-details
                label="Zone"
                :items="zoneItems"
                v-model="filterMapId"
            />
        </div>

        <div v-if="loading" class="home-leaderboard__loading">
            Chargement...
        </div>

        <div v-else>
            <div v-if="rows.length === 0" class="home-leaderboard__empty">
                Aucun score pour l’instant.
            </div>
            <div v-else class="home-leaderboard__table">
                <div class="home-leaderboard__row home-leaderboard__row--head">
                    <span>#</span>
                    <span>Nom</span>
                    <span class="right">Points</span>
                    <span class="right">Zone</span>
                </div>
                <div
                    v-for="(r, idx) in rows"
                    :key="r.id"
                    class="home-leaderboard__row"
                >
                    <span class="muted">{{ idx + 1 }}</span>
                    <span class="name">{{ r.name }}</span>
                    <span class="right points">{{ formatPoints(r.points) }}</span>
                    <span class="right zone">{{ r.mapName }}</span>
                </div>
            </div>
            <div class="home-leaderboard__foot muted">
                Source: {{ sourceLabel }}
            </div>
        </div>
    </div>
</template>

<script>
import { fetchLeaderboardEntries, getLocalHistoryLeaderboard } from '@/utils/leaderboard';

export default {
    name: 'HomeLeaderboard',
    props: {
        // [{ id, nameLocate/name }]
        zoneMaps: {
            type: Array,
            required: true,
        },
        defaultMapId: {
            type: String,
            default: null,
        },
        limit: {
            type: Number,
            default: 25,
        },
    },
    data() {
        return {
            filterMapId: null,
            rows: [],
            loading: true,
            source: 'local',
        };
    },
    computed: {
        zoneItems() {
            const items = [
                { text: 'Toutes les zones', value: null },
                ...this.zoneMaps.map((m) => ({
                    text: m.nameLocate || m.name,
                    value: m.id,
                })),
            ];
            return items;
        },
        sourceLabel() {
            return this.source === 'global'
                ? 'palmarès global (serveur)'
                : 'historique local (cet appareil)';
        },
    },
    watch: {
        filterMapId() {
            this.load();
        },
        zoneMaps() {
            // If we receive maps after async load, set a reasonable default once.
            if (this.filterMapId === null && this.defaultMapId) {
                this.filterMapId = null;
            }
        },
    },
    async mounted() {
        this.filterMapId = null;
        await this.load();
    },
    methods: {
        formatPoints(n) {
            const v = Number(n) || 0;
            return v.toLocaleString('fr-CA');
        },
        async load() {
            this.loading = true;
            const limit = this.limit;
            const mapId = this.filterMapId;

            const globalRows = await fetchLeaderboardEntries({ mapId, limit });
            if (Array.isArray(globalRows)) {
                this.rows = globalRows;
                this.source = 'global';
                this.loading = false;
                return;
            }

            this.rows = getLocalHistoryLeaderboard({ mapId, limit });
            this.source = 'local';
            this.loading = false;
        },
    },
};
</script>

<style scoped lang="scss">
.home-leaderboard {
    padding: 1.2rem 1.2rem 0.9rem;
}

.home-leaderboard__header {
    display: grid;
    gap: 0.75rem;
}

.home-leaderboard__title {
    margin: 0;
    font-size: 1.35rem;
}

.home-leaderboard__subtitle {
    margin: 0.25rem 0 0;
    opacity: 0.85;
}

.home-leaderboard__select {
    max-width: 360px;
}

.home-leaderboard__loading,
.home-leaderboard__empty {
    padding: 1rem 0.25rem;
    font-weight: 700;
    opacity: 0.85;
}

.home-leaderboard__table {
    margin-top: 0.75rem;
    border-top: 2px solid var(--brand-border);
}

.home-leaderboard__row {
    display: grid;
    grid-template-columns: 2.5rem 1fr 7.5rem 1fr;
    gap: 0.75rem;
    align-items: center;
    padding: 0.65rem 0.25rem;
    border-bottom: 1px solid rgba(15, 23, 42, 0.12);
}

.home-leaderboard__row--head {
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-size: 0.78rem;
    opacity: 0.8;
}

.muted {
    opacity: 0.75;
}

.right {
    text-align: right;
}

.name,
.zone {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.points {
    font-weight: 900;
}

.home-leaderboard__foot {
    margin-top: 0.6rem;
    font-size: 0.85rem;
}

@media (min-width: 960px) {
    .home-leaderboard__header {
        grid-template-columns: 1fr auto;
        align-items: end;
    }
}
</style>
